/**
 * AddInstance — the CCA "Add instance" card on Manage Portfolio: describe one
 * instance (where it runs, what it is, how many and for how long) and add it
 * to the portfolio.
 *
 * Ported from legacy ManageAccount.vue (CCA layout :70-119, commonFields
 * :323-401, fieldsForCCA :402-443, updateFormData :582-608, addInstanceClick
 * :612-699). The fields, rules and the row sent on Add live in
 * ../ccaFormRules.js so they are tested without rendering.
 *
 * Same props as components/eia/AddInstance, so the page swaps one for the
 * other by product without changing how it wires the card (option lists from
 * useInstanceOptions, onAdd, disabled, onHelp, onFindReplace,
 * findReplaceBlockedBy). Only the fields differ — a CCA instance is described
 * by Quantity + Total Number of Hours per Month instead of EIA's usage figures.
 *
 * Kept from legacy: two rows — Region, Size (Type on OCI), Operating System,
 * UUID / Instance Name (+ OCPU and Memory on OCI, disabled unless the type is
 * a flex shape), then Quantity, Total Number of Hours per Month, Pricing Model
 * and the action buttons; changing Region clears Size and OCPU / Memory;
 * picking a non-flex OCI type clears OCPU / Memory; a blank UUID gets a random
 * one; the form resets after a successful add.
 */
import { useState } from 'react';

import {
  CCA_FIELD,
  buildCcaInstance,
  ccaFieldsFor,
  emptyCcaForm,
  instanceTypeLabel,
  validateCcaForm,
} from '@components/cca/ccaFormRules';
import { Dropdown } from '@components/common/Dropdown';
import { FloatingTooltip } from '@components/common/FloatingTooltip';
import { FindReplaceIcon, InfoIcon, PlusIcon } from '@components/common/icons';
import { NumberField } from '@components/common/NumberField';
import { TextField } from '@components/common/TextField';
import { OS_OPTIONS, PRICING_MODEL_OPTIONS, isOciFlex } from '@components/eia/portfolioFormRules';
import { Button, Card, CardBody, Tooltip } from '@components/ui';

import styles from './AddInstance.module.css';

/** Legacy's tooltip on the help button, also its accessible name. */
const HELP_LABEL = 'Data correction & adjustment guidelines';

/** DOM ids must not contain spaces or brackets; the API keys do. */
const domId = (field) => `cca-add-instance-${field.replace(/[^a-z0-9]+/gi, '-')}`;

/** Each field's placeholder (legacy shows none; the app's fields all have one). */
function placeholderFor(id, provider) {
  switch (id) {
    case CCA_FIELD.REGION:
      return 'Select region';
    case CCA_FIELD.INSTANCE_TYPE:
      return `Select ${instanceTypeLabel(provider).toLowerCase()}`;
    case CCA_FIELD.UUID:
      return 'Enter UUID / Instance Name';
    case CCA_FIELD.OCPU:
      return 'Enter OCPU';
    case CCA_FIELD.MEMORY:
      return 'Enter memory';
    case CCA_FIELD.QUANTITY:
      return 'Enter quantity';
    case CCA_FIELD.HOURS:
      return 'Enter total hours';
    case CCA_FIELD.PRICING_MODEL:
      return 'Select pricing model';
    default:
      return undefined;
  }
}

/**
 * A field with its hover hint. Floating (drawn over the page) so a hint near
 * the right edge can never push out a sideways scrollbar; hover only, so it
 * never covers the field being typed in (legacy hides them while focused).
 */
function FieldTip({ tip, children }) {
  if (!tip) return children;
  return (
    <FloatingTooltip label={tip} side="bottom" hoverOnly className={styles.fieldTip}>
      {children}
    </FloatingTooltip>
  );
}

export function AddInstance({
  provider,
  regions = [],
  regionsLoading = false,
  regionsError,
  instanceTypes = [],
  instanceTypesLoading = false,
  instanceTypesError,
  onRegionChange,
  onAdd,
  onHelp,
  /** Opens Find and Replace; no button without it (permission-gated by the page). */
  onFindReplace,
  /** Why Find and Replace can't run right now (shown as its title), or null. */
  findReplaceBlockedBy = null,
  disabled = false,
}) {
  const [form, setForm] = useState(emptyCcaForm);
  const [errors, setErrors] = useState({});
  // While the page has the row checked by the server (/input/validate).
  const [adding, setAdding] = useState(false);

  const { common, cca } = ccaFieldsFor(provider, form[CCA_FIELD.INSTANCE_TYPE]);

  /** Update one field (plus any it resets) and drop their errors — the user is fixing it. */
  const set = (field, value, extra = {}) => {
    setForm((f) => ({ ...f, [field]: value, ...extra }));
    setErrors((e) => {
      const cleared = [field, ...Object.keys(extra)];
      if (!cleared.some((key) => e[key])) return e;
      const rest = { ...e };
      cleared.forEach((key) => delete rest[key]);
      return rest;
    });
  };

  /** Legacy updateFormData :596-601 — the old Size may not exist in the new region. */
  const changeRegion = (region) => {
    set(CCA_FIELD.REGION, region, {
      [CCA_FIELD.INSTANCE_TYPE]: '',
      [CCA_FIELD.OCPU]: '',
      [CCA_FIELD.MEMORY]: '',
    });
    onRegionChange?.(region);
  };

  /** Legacy updateFormData :602-607 — a non-flex OCI type takes no OCPU / Memory. */
  const changeInstanceType = (type) => {
    const reset = isOciFlex(provider, type)
      ? {}
      : { [CCA_FIELD.OCPU]: '', [CCA_FIELD.MEMORY]: '' };
    set(CCA_FIELD.INSTANCE_TYPE, type, reset);
  };

  /**
   * `onAdd` may be async and returns false when the row was NOT added (the
   * server refused it): then the form keeps its values so the user can fix
   * and retry, instead of retyping everything.
   */
  const add = async () => {
    const found = validateCcaForm(form, provider);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setAdding(true);
    try {
      const added = await onAdd?.(buildCcaInstance(form, provider));
      if (added === false) return;
      setForm(emptyCcaForm());
      // The form's region is cleared — tell the page, so the size list it
      // holds follows.
      onRegionChange?.('');
    } finally {
      setAdding(false);
    }
  };

  /** The option list, change handler and extras for one select field. */
  const selectProps = (id) => {
    switch (id) {
      case CCA_FIELD.REGION:
        return {
          options: regions,
          loading: regionsLoading,
          searchable: true,
          onChange: changeRegion,
          // A field error ("Required") first; otherwise a failed load.
          error: errors[id] ?? regionsError,
        };
      case CCA_FIELD.INSTANCE_TYPE:
        return {
          options: instanceTypes,
          loading: instanceTypesLoading,
          searchable: true,
          onChange: changeInstanceType,
          // Sizes depend on the region, so pick that first.
          disabled: disabled || !form[CCA_FIELD.REGION],
          error: errors[id] ?? instanceTypesError,
        };
      case CCA_FIELD.OS:
        return { options: OS_OPTIONS, onChange: (v) => set(id, v) };
      default:
        return { options: PRICING_MODEL_OPTIONS, onChange: (v) => set(id, v) };
    }
  };

  const renderField = (field) => {
    const { id, label, kind, required, tooltip } = field;
    const shared = {
      id: domId(id),
      label,
      required,
      placeholder: placeholderFor(id, provider),
      value: form[id],
      disabled: disabled || Boolean(field.disabled),
      error: errors[id],
    };
    let control;
    if (kind === 'select') {
      control = <Dropdown {...shared} {...selectProps(id)} />;
    } else if (kind === 'number') {
      control = <NumberField {...shared} onChange={(e) => set(id, e.target.value)} min={0} />;
    } else {
      control = (
        <TextField {...shared} onChange={(e) => set(id, e.target.value)} autoComplete="off" />
      );
    }
    return (
      <FieldTip key={id} tip={tooltip}>
        {control}
      </FieldTip>
    );
  };

  return (
    <Card className={styles.card}>
      <CardBody className={styles.body}>
        <section className={styles.addInstance} aria-labelledby="cca-add-instance-title">
          <header className={styles.header}>
            <div className={styles.heading}>
              <h2 id="cca-add-instance-title" className={styles.title}>
                Add Instance
              </h2>
              <p className={styles.subtitle}>Define one instance to add to this portfolio.</p>
            </div>
            {/* Legacy's help button (ActionButtons.vue:22-34), drawn as the
                design's still ⓘ — never disabled, shown only when the page
                passes onHelp (permission-gated). Tooltip side="left": the
                button sits at the page's right edge. */}
            {onHelp ? (
              <Tooltip label={HELP_LABEL} side="left">
                <button
                  type="button"
                  className={styles.help}
                  onClick={onHelp}
                  aria-label={HELP_LABEL}
                >
                  <InfoIcon />
                </button>
              </Tooltip>
            ) : null}
          </header>

          <fieldset className={styles.group} disabled={disabled}>
            <legend className="sr-only">Instance details</legend>
            <div className={styles.rows}>
              {/* Row 1 — legacy commonFields. */}
              <div className={styles.grid}>{common.map(renderField)}</div>

              {/* Row 2 — legacy fieldsForCCA, then the action buttons. */}
              <div className={styles.grid}>
                {cca.map(renderField)}
                <div className={styles.addCell}>
                  <Button
                    variant="primary"
                    className={styles.addButton}
                    startIcon={<PlusIcon />}
                    onClick={add}
                    loading={adding}
                    disabled={disabled || adding}
                  >
                    Add instance
                  </Button>
                  {/* Legacy's order: Add, then Find and Replace (ActionButtons.vue:4-21). */}
                  {onFindReplace ? (
                    <Button
                      variant="secondary-solid"
                      className={styles.addButton}
                      startIcon={<FindReplaceIcon />}
                      onClick={onFindReplace}
                      disabled={Boolean(findReplaceBlockedBy)}
                      title={findReplaceBlockedBy ?? undefined}
                    >
                      Find and Replace
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </fieldset>
        </section>
      </CardBody>
    </Card>
  );
}

export default AddInstance;
